import { useNavigationState } from "@/zustand/NavigationState";

import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import { LoaderCircle, Pin } from "lucide-react";
import { useMessageNavigation } from "../messages/hooks/useMessageNavigation";

import { useCallback, useEffect, useState } from "react";

import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/primitives/carousel";
import PinnedMessage from "./PinnedMessage";
import { cn } from "@/lib/utils";
import { getPinnedMessagesByChatIdFromLocalDB } from "@/indexedDB/services/pinnedMessages";

export default function PinnedMessageList() {
  const activeChatId = useNavigationState((state) => state.activeChatId);

  const [api, setApi] = useState<CarouselApi>();
  const [loading, setLoading] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  const messages = useSafeLiveQuery(
    () => getPinnedMessagesByChatIdFromLocalDB(activeChatId || ""),
    [activeChatId],
    [],
  );

  const { navigateToMessage } = useMessageNavigation(activeChatId || "");

  const calcProgress = useCallback((val: number) => {
    const progress = Math.round(Math.max(0, Math.min(1, val)) * 100);
    return progress;
  }, []);

  // Reset carousel when messages change
  useEffect(() => {
    if (!api || !messages || messages.length === 0) {
      return;
    }

    // Recompute after messages change
    api.reInit();

    // Reset to last message if current index is out of bounds
    if (currentIndex >= messages.length - 1) {
      setCurrentIndex(messages.length - 1);
      api.scrollTo(messages.length - 1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Track current index and progress
  useEffect(() => {
    if (!api) {
      return;
    }

    // Set initial index and progress
    setCurrentIndex(api.selectedScrollSnap());
    setScrollProgress(calcProgress(api.scrollProgress()));

    // Listen for slide changes
    const onSelect = () => {
      // console.log("Slide selected:", api.selectedScrollSnap());
      setCurrentIndex(api.selectedScrollSnap());
    };

    // Listen for scroll
    const onScroll = () => {
      // console.log("Scroll progress:", api.scrollProgress());
      setScrollProgress(calcProgress(api.scrollProgress()));
      // setCurrentIndex(api.selectedScrollSnap());
    };

    // Listen for settle // USED WHEN ANIMATION IS NEEDED
    // const onSettle = () => {
    //   console.log("Settle complete");
    //   // setIsAnimating(false);
    //   setScrollProgress(calcProgress(api.scrollProgress()));
    // };

    api.on("select", onSelect);
    api.on("scroll", onScroll);
    // api.on("settle", onSettle);

    return () => {
      api.off("select", onSelect);
      api.off("scroll", onScroll);
      // api.off("settle", onSettle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, activeChatId, messages]);

  const handleClick = async () => {
    if (!api || !messages || messages.length === 0 || loading) {
      return;
    }

    const currentMessageId = messages[currentIndex]?.id;

    if (currentMessageId) {
      setLoading(true);
      await navigateToMessage(currentMessageId)
        .then(() => {
          // Small delay to ensure smooth scrolling
          setTimeout(() => {
            api.scrollNext();
          }, 100);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  if (!messages || messages.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "hidden shadow-md",
        !api ? "opacity-0" : "block opacity-100 fade-in",
      )}
    >
      <div
        className="flex cursor-pointer select-none items-center lg:mx-auto lg:max-w-[1000px]"
        onClick={handleClick}
      >
        <div className="relative mx-2 block h-24 w-1 shrink-0 overflow-hidden rounded-full bg-primary/20">
          <div
            className="absolute bottom-0 left-0 w-full bg-secondary"
            style={{
              height: `${!scrollProgress ? 100 : scrollProgress}%`,
              // height: `${scrollProgress}%`,
              transition: "transform 150ms linear",
            }}
          ></div>
        </div>

        {loading ? (
          <LoaderCircle className="mb-auto mt-3 size-4 shrink-0 animate-spin" />
        ) : (
          <Pin className="mb-auto mt-3 size-4 shrink-0 -rotate-45" />
        )}

        <Carousel
          className="size-full"
          orientation="vertical"
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
            dragFree: true,
            slidesToScroll: 1,
            watchDrag: false,
            axis: "y",
            // startIndex: messages.length - 1,
            duration: 0,
            skipSnaps: true,
          }}
        >
          <CarouselContent
            className={cn(
              "mt-0 h-28 w-full align-middle",
              loading && "opacity-70",
            )}
          >
            {messages?.map((message) => (
              <CarouselItem
                key={message.id}
                className="flex min-w-0 items-center py-2 align-middle"
              >
                <PinnedMessage message={message} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
}
